// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🏋️ EXERCISE SERVICE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

import { supabase } from '../lib/supabase';
import type {
  Exercise,
  ExerciseWithCreator,
  CreateExerciseParams,
  ExerciseRating,
  ExerciseCategory,
  Difficulty,
} from '../types';

// ============================================================================
// CREATE
// ============================================================================

export async function createExercise(params: CreateExerciseParams): Promise<Exercise> {
  const { data, error } = await supabase
    .from('exercises')
    .insert({
      created_by: params.created_by,
      club_id: params.club_id || null,
      is_global: params.is_global || false,
      title: params.title,
      description: params.description || null,
      instructions: params.instructions || null,
      images: params.images || [],
      video_url: params.video_url || null,
      category: params.category || null,
      difficulty: params.difficulty || null,
      scoring_type: params.scoring_type || null,
      scoring_config: params.scoring_config || null,
      is_public: params.is_public || false,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Clone exercise (copy to personal library)
export async function cloneExercise(sourceExerciseId: number, userId: number): Promise<Exercise> {
  // Get source exercise
  const { data: source, error: fetchError } = await supabase
    .from('exercises')
    .select('*')
    .eq('id', sourceExerciseId)
    .single();

  if (fetchError) throw fetchError;

  // Create personal copy
  const { data, error } = await supabase
    .from('exercises')
    .insert({
      created_by: userId,
      club_id: null, // Always personal
      is_global: false,
      title: `${source.title} (Kopie)`,
      description: source.description,
      instructions: source.instructions,
      images: source.images,
      video_url: source.video_url,
      category: source.category,
      difficulty: source.difficulty,
      scoring_type: source.scoring_type,
      scoring_config: source.scoring_config,
      is_public: false,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

// ============================================================================
// READ
// ============================================================================

// Get single exercise
export async function getExercise(id: number): Promise<ExerciseWithCreator | null> {
  const { data, error } = await supabase
    .from('exercises')
    .select(`
      *,
      creator:players!created_by(id, prename, name, email, profile_picture_url),
      club:clubs(id, name)
    `)
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  return data;
}

// Get personal exercises
export async function getPersonalExercises(playerId: number): Promise<Exercise[]> {
  const { data, error } = await supabase
    .from('exercises')
    .select('*')
    .eq('created_by', playerId)
    .eq('is_global', false)
    .is('club_id', null)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

// Get club exercises
export async function getClubExercises(clubId: number): Promise<Exercise[]> {
  const { data, error } = await supabase
    .from('exercises')
    .select('*')
    .eq('club_id', clubId)
    .eq('is_global', false)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

// Get global exercises
export async function getGlobalExercises(): Promise<Exercise[]> {
  const { data, error } = await supabase
    .from('exercises')
    .select('*')
    .eq('is_global', true)
    .eq('is_public', true)
    .order('usage_count', { ascending: false });

  if (error) throw error;
  return data || [];
}

// Search exercises
export async function searchExercises(query: string, filters?: {
  category?: ExerciseCategory;
  difficulty?: Difficulty;
  is_global?: boolean;
  club_id?: number;
}): Promise<Exercise[]> {
  let queryBuilder = supabase
    .from('exercises')
    .select('*')
    .or(`title.ilike.%${query}%,description.ilike.%${query}%`);

  if (filters?.category) {
    queryBuilder = queryBuilder.eq('category', filters.category);
  }

  if (filters?.difficulty) {
    queryBuilder = queryBuilder.eq('difficulty', filters.difficulty);
  }

  if (filters?.is_global !== undefined) {
    queryBuilder = queryBuilder.eq('is_global', filters.is_global);
  }

  if (filters?.club_id) {
    queryBuilder = queryBuilder.eq('club_id', filters.club_id);
  }

  const { data, error } = await queryBuilder.order('usage_count', { ascending: false });

  if (error) throw error;
  return data || [];
}

// Get top exercises (by usage & rating)
export async function getTopExercises(limit: number = 10): Promise<Exercise[]> {
  const { data, error } = await supabase
    .from('exercises')
    .select('*')
    .eq('is_public', true)
    .order('usage_count', { ascending: false })
    .order('average_rating', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data || [];
}

// ============================================================================
// UPDATE
// ============================================================================

export async function updateExercise(
  id: number,
  updates: Partial<CreateExerciseParams>
): Promise<Exercise> {
  const { data, error } = await supabase
    .from('exercises')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// ============================================================================
// DELETE
// ============================================================================

export async function deleteExercise(id: number): Promise<void> {
  const { error } = await supabase
    .from('exercises')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// ============================================================================
// RATINGS
// ============================================================================

export async function rateExercise(
  exerciseId: number,
  playerId: number,
  rating: number,
  comment?: string
): Promise<ExerciseRating> {
  const { data, error } = await supabase
    .from('exercise_ratings')
    .upsert({
      exercise_id: exerciseId,
      player_id: playerId,
      rating,
      comment: comment || null,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getExerciseRatings(exerciseId: number): Promise<ExerciseRating[]> {
  const { data, error } = await supabase
    .from('exercise_ratings')
    .select(`
      *,
      player:players(id, prename, name, profile_picture_url)
    `)
    .eq('exercise_id', exerciseId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function getUserRatingForExercise(
  exerciseId: number,
  playerId: number
): Promise<ExerciseRating | null> {
  const { data, error } = await supabase
    .from('exercise_ratings')
    .select('*')
    .eq('exercise_id', exerciseId)
    .eq('player_id', playerId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  return data;
}

// ============================================================================
// STORAGE (Images)
// ============================================================================

export async function uploadExerciseImage(
  exerciseId: number,
  file: File
): Promise<string> {
  const fileExt = file.name.split('.').pop();
  const fileName = `${exerciseId}/${Date.now()}.${fileExt}`;

  const { error } = await supabase.storage
    .from('exercise-images')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (error) throw error;

  // Get public URL
  const { data: { publicUrl } } = supabase.storage
    .from('exercise-images')
    .getPublicUrl(fileName);

  return publicUrl;
}

export async function deleteExerciseImage(url: string): Promise<void> {
  // Extract path from URL
  const path = url.split('/exercise-images/')[1];

  const { error } = await supabase.storage
    .from('exercise-images')
    .remove([path]);

  if (error) throw error;
}

// Add image to exercise
export async function addImageToExercise(exerciseId: number, imageUrl: string): Promise<Exercise> {
  // Get current images
  const { data: exercise } = await supabase
    .from('exercises')
    .select('images')
    .eq('id', exerciseId)
    .single();

  if (!exercise) throw new Error('Exercise not found');

  const currentImages = exercise.images || [];
  const updatedImages = [...currentImages, imageUrl];

  return updateExercise(exerciseId, { images: updatedImages });
}

// Remove image from exercise
export async function removeImageFromExercise(
  exerciseId: number,
  imageUrl: string
): Promise<Exercise> {
  // Get current images
  const { data: exercise } = await supabase
    .from('exercises')
    .select('images')
    .eq('id', exerciseId)
    .single();

  if (!exercise) throw new Error('Exercise not found');

  const currentImages = exercise.images || [];
  const updatedImages = currentImages.filter((url: string) => url !== imageUrl);

  // Delete from storage
  await deleteExerciseImage(imageUrl);

  return updateExercise(exerciseId, { images: updatedImages });
}

